import { Features } from "@app/feature-flags";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { ServicingAnalysisGranularityEnum } from "@app/templates";
import { Roles, UserInfo } from "@app/user";
import { AdequacyDetail } from "./adequacyDetail";
import { SpecialtyGroupExceptionStatus } from "@app/projects/results/common/specialtyGroupException";
import { AdequacyAggregate } from "./adequacyAggregate";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import Project from "@app/projects/project";
import ProjectRunConfiguration from "../../projectRunConfiguration";

export const getServicingGranularityHeader = (servicingGranularity: ServicingAnalysisGranularityEnum): string => {
    switch (servicingGranularity) {
        case ServicingAnalysisGranularityEnum.State:
            return "In State";
        case ServicingAnalysisGranularityEnum.Region:
        case ServicingAnalysisGranularityEnum.Unknown:
            return "In Region";
        case ServicingAnalysisGranularityEnum.County:
        default:
            return "In County";
    }
}

export const getMarketAnalysisTooltip = (projectRunConfiguration: ProjectRunConfiguration): string => {
    let tooltipMesage = "using the Market provider selection.";
    const useNetworkProviders = projectRunConfiguration.useNetworkProvidersForMarketAnalysis;
    const useProspectProviders = projectRunConfiguration.useProspectProvidersForMarketAnalysis;

    if (useNetworkProviders && useProspectProviders) {
        tooltipMesage = "the aggregate of Network, Prospect and Market provider selections.";
    } else if (useNetworkProviders) {
        tooltipMesage = "the aggregate of Network and Market provider selections.";
    } else if (useProspectProviders) {
        tooltipMesage = "the aggregate of Prospect and Market provider selections.";
    }

    return "Market Analysis results reflect " + tooltipMesage;
}

export const getFillableGapsIndicatorTooltip = (projectRunConfiguration: ProjectRunConfiguration): string => {
    let tooltipMesage = "";
    const useProspectProviders = !!projectRunConfiguration.prospectProviderSetId;
    const useMarketProviders = !!projectRunConfiguration.marketProviderSetId;

    if (useMarketProviders && useProspectProviders) {
        tooltipMesage = "the aggregate of Network, Prospect and Market provider selections.";
    } else if (useMarketProviders) {
        tooltipMesage = "the aggregate of Network and Market provider selections.";
    } else if (useProspectProviders) {
        tooltipMesage = "the aggregate of Network and Prospect provider selections.";
    }

    return "Fillable Gap Indicator results reflect " + tooltipMesage;
}

export const dataHasExceptions = (data: AdequacyDetail[] | AdequacyAggregate[]): boolean => {
    if (!!data && data[0]?.hasOwnProperty("exceptionCount")) {
        return (data as AdequacyAggregate[]).some((d) => d.exceptionCount > 0);
    }

    return (data as AdequacyDetail[])?.some((d) => (!!d?.exception && !d.exception.isUnprocessed
        && (d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Appealed
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Approved
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Denied
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Pending
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Submitted)));
}

export const dataHasGaps = (data: AdequacyDetail[] | AdequacyAggregate[], cmsCreditIsApplying: boolean): boolean => {
    if (!!data && data[0]?.hasOwnProperty("exceptionCount")) {
        return (data as AdequacyAggregate[]).some((d) => d.gapCount > 0 || (cmsCreditIsApplying && d.gapCountCMSCredit > 0));
    }

    return (data as AdequacyDetail[])?.some((d) => !!d && d.accessSummary.memberCount > 0 && !(d.metCriteria || (d.applicationCreditMetCriteria && cmsCreditIsApplying)));
}

export const showPatternOfCare = (user: UserInfo, enabledFeatureFlags: Set<string>, isComparingResults: boolean, project: Project, runConfiguration: ProjectRunConfigurationResult, records?: AdequacyDetail[] | AdequacyAggregate[], hasExceptions?: boolean, cmsCreditIsApplying?: boolean): boolean => {
    return user.roles.includes(Roles.PATTERN_OF_CARE_USER)
        && enabledFeatureFlags.has(Features.MANAGE_HSD_PROVIDERS)
        && !isComparingResults
        && (runConfiguration.isLatestPublished || !runConfiguration.datePublished)
        && project.showPatternOfCareProviders
        && !runConfiguration.isProjected && !runConfiguration.isProjectedTermed
        && runConfiguration.networkAssessmentAnalysisType !== NetworkAssessmentAnalysisType.Opportunity
        && ((!!records && (dataHasGaps(records, cmsCreditIsApplying) || dataHasExceptions(records))) || hasExceptions);
}
